import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { RadioButton, RadioButtonGroup } from '@sunwebgroup/uds-ui/radio-button'
import * as React from 'react'
import { toSnakeCase } from '@/helpers/string-helpers'
import { useIntl } from '@/intl'
import {
  FilterContainer,
  FilterDescription,
  FilterItemOption,
  FilterItemOptionContent,
  FilterItemOptionControl,
  FilterItemOptionTrailing,
  FilterItemOptionWrapper,
  FilterItemsContainer,
  FilterLabel,
  FilterOptionPrice,
  FilterOptionValue,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/FilterableCars/Filters/Filters'
import { createCarEvent } from '@/scopes/checkout/domain/analytics'
import { updateSingleValueFilter } from '@/scopes/checkout/filterableCars/domain/actions'
import { useFilter } from '@/scopes/checkout/filterableCars/domain/hooks'
import { CarFilterType } from '@/scopes/checkout/filterableCars/domain/types'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

type DepositFilterType = 'all_cars' | 'credit_card' | 'debit_cash' | 'cash' | 'none'

function filterTranslationTextKeys(key: DepositFilterType): string | undefined {
  const mapping: Record<string, string> = {
    all_cars: 'checkout_steps_ancillaries_car_rental_filtering_filters_deposit_payment_method_type_all_cars_text',
    credit_card: 'checkout_steps_ancillaries_car_rental_filtering_filters_deposit_payment_method_type_credit_card_text',
    debit_card: 'checkout_steps_ancillaries_car_rental_filtering_filters_deposit_payment_method_type_debit_card_text',
    debit_cash: 'checkout_steps_ancillaries_car_rental_filtering_filters_deposit_payment_method_type_debit_cash_text',
    none: 'checkout_steps_ancillaries_car_rental_filtering_filters_deposit_payment_method_type_none_text',
  }
  return mapping[key]
}

export function DepositTypeFilter() {
  const { filter, setFilterValue } = useFilter(CarFilterType.DepositPaymentMethod)
  const intl = useIntl()
  const { sendEvent } = useAnalytics()

  const handleFilterValueChange = React.useCallback(
    (value: string) => {
      if (!value)
        return
      if (!filter)
        return
      setFilterValue(updateSingleValueFilter({ value })(filter))

      sendEvent(
        createCarEvent({
          result: 'Filter selected',
          type: value,
          description: 'DepositPaymentMethod',
          element: 'radiobutton',
          validated: true,
        }),
      )
    },
    [sendEvent, filter, setFilterValue],
  )

  if (!filter)
    return null

  const { filterOptions, selectedFilter } = filter

  return (
    <FilterContainer>
      <FilterLabel>{intl.formatMessage({ id: `checkout_steps_ancillaries_car_rental_filtering_filters_deposit_payment_method_type_label` })}</FilterLabel>
      <FilterDescription>{intl.formatMessage({ id: `checkout_steps_ancillaries_car_rental_filtering_filters_deposit_payment_method_type_supporting_text` })}</FilterDescription>
      <RadioButtonGroup value={selectedFilter ?? undefined} onValueChange={handleFilterValueChange}>
        <FilterItemsContainer>
          {filterOptions.map((option) => {
            const { value, name, quantity, startingPrice } = option
            const filterTextKey = filterTranslationTextKeys(toSnakeCase(name) as DepositFilterType) || ''

            return (
              <FilterItemOptionWrapper key={value}>
                <ListItem variant="outlined">
                  <FilterItemOption>
                    <FilterItemOptionControl>
                      <RadioButton value={value} />
                    </FilterItemOptionControl>
                    <FilterItemOptionContent>
                      <FilterOptionValue>
                        {filterTextKey ? intl.formatMessage({ id: filterTextKey }) : name}
                        {' '}
                        (
                        {quantity ?? 0}
                        )
                      </FilterOptionValue>
                    </FilterItemOptionContent>
                    {startingPrice !== undefined && startingPrice !== null && (quantity && quantity > 0)
                      ? (
                          <FilterItemOptionTrailing>
                            <FilterOptionPrice>
                              {intl.formatMessage({ id: `checkout_steps_ancillaries_car_rental_filtering_filters_from_starting_price_text` }, { price: intl.formatTruncatedCurrency(startingPrice) })}
                            </FilterOptionPrice>
                          </FilterItemOptionTrailing>
                        )
                      : ''}
                  </FilterItemOption>
                </ListItem>
              </FilterItemOptionWrapper>
            )
          })}
        </FilterItemsContainer>
      </RadioButtonGroup>
    </FilterContainer>
  )
}
